import React, { useState, useEffect } from 'react';
import { hasCookieConsent, setCookieConsent } from '../utils/cookieUtils';

const CookiePreferencesPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = hasCookieConsent();
    if (!hasConsent) {
      // Show popup after a brief delay to avoid jarring experience
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookieConsent(true);
    closePopup();
  };

  const handleDecline = () => {
    setCookieConsent(false);
    closePopup();
  };

  const closePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={closePopup}
      />
      
      {/* Popup */}
      <div 
        className={`fixed bottom-0 left-0 right-0 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-50 
          transform transition-all duration-300 ease-out ${
          isClosing 
            ? 'translate-y-full md:translate-y-0 md:translate-x-full opacity-0' 
            : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="bg-white border-2 border-gray-200 shadow-xl md:rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#335D95] text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Cookie Preferences</h2>
              <button
                onClick={closePopup}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close cookie preferences"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-gray-700 text-sm mb-4 leading-relaxed">
              We use cookies to enhance your browsing experience, analyze site traffic, and provide personalized content. 
              By accepting cookies, you help us improve our website and provide better services.
            </p>
            
            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Essential Cookies</p>
                  <p className="text-xs text-gray-600">Required for basic website functionality</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Analytics Cookies</p>
                  <p className="text-xs text-gray-600">Help us understand how visitors use our site</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Marketing Cookies</p>
                  <p className="text-xs text-gray-600">Used to provide relevant advertising</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAccept}
                className="flex-1 bg-[#335D95] hover:bg-[#2a4a7a] text-white px-4 py-2.5 rounded-md 
                  text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 
                  focus:ring-[#335D95] focus:ring-offset-2"
              >
                Accept All Cookies
              </button>
              
              <button
                onClick={handleDecline}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2.5 rounded-md 
                  text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 
                  focus:ring-gray-300 focus:ring-offset-2"
              >
                Decline
              </button>
            </div>

            {/* Footer links */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                <a 
                  href="/privacy" 
                  className="hover:text-[#335D95] transition-colors underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                <a 
                  href="/cookies" 
                  className="hover:text-[#335D95] transition-colors underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePreferencesPopup;
